// ClientSubscriptionsTab — substitui PlaceholderTab "Assinaturas Owl" no detalhe
// do cliente. Lista assinaturas owl_managed do cliente com link pro detalhe.

import { AlertTriangle, Calendar, Package, Plus, Repeat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDayBR } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { useCharges } from '../../hooks/use-charges';
import type { SubscriptionLite } from '../../lib/subscriptions-api';
import { PillButton } from './PillButton';

const STATUS_LABEL: Record<string, string> = {
  ativa: 'Ativa',
  em_teste: 'Em teste',
  trial_expired: 'Trial expirado',
  aguardando_pagamento: 'Aguardando pagamento',
  inadimplente: 'Inadimplente',
  suspensa: 'Suspensa',
  pending_cancellation: 'Cancelamento agendado',
  cancelada: 'Cancelada',
  pending_provisioning: 'Aguardando provisioning',
  encerrada: 'Encerrada',
};

const STATUS_TONE: Record<string, string> = {
  ativa: 'bg-good/10 text-good border-good/40',
  em_teste: 'bg-info/10 text-info border-info/40',
  trial_expired: 'bg-warn/10 text-warn border-warn/40',
  aguardando_pagamento: 'bg-warn/10 text-warn border-warn/40',
  inadimplente: 'bg-bad/10 text-bad border-bad/40',
  suspensa: 'bg-bad/10 text-bad border-bad/40',
  pending_cancellation: 'bg-warn/10 text-warn border-warn/40',
  cancelada: 'bg-panel-3 text-muted border-line',
  pending_provisioning: 'bg-info/10 text-info border-info/40',
  encerrada: 'bg-panel-3 text-muted border-line',
};

function formatBRL(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

export interface ClientSubscriptionsTabProps {
  clientId: string;
  items: SubscriptionLite[];
  isLoading: boolean;
  isError: boolean;
  /** 'owl_managed' = mostra botão criar; 'saas_direct' = só leitura */
  variant?: 'owl_managed' | 'saas_direct';
}

export function ClientSubscriptionsTab({
  clientId,
  items,
  isLoading,
  isError,
  variant = 'owl_managed',
}: ClientSubscriptionsTabProps): JSX.Element {
  const isOwlManaged = variant === 'owl_managed';
  const navigate = useNavigate();

  /**
   * Cobranças VENCIDAS do cliente, por assinatura.
   *
   * Sem isto o card mentia por omissão: "Ativa · próxima cobrança 11/09" passa a
   * impressão de que está tudo em dia, enquanto o cliente devia R$34,99 desde
   * 09/08 (caso real 2026-08-12). Quem olha o cadastro precisa ver a dívida sem
   * ter que abrir a tela de cobranças e cruzar na mão.
   */
  const overdueQuery = useCharges({
    clientId,
    status: 'vencida',
    scope: 'todas',
    page: 1,
    pageSize: 50,
  });
  const overdueBySubscription = new Map<string, { count: number; total: number }>();
  for (const c of overdueQuery.data?.data ?? []) {
    if (!c.subscriptionId) continue;
    const acc = overdueBySubscription.get(c.subscriptionId) ?? { count: 0, total: 0 };
    acc.count += 1;
    acc.total += Number(c.amount);
    overdueBySubscription.set(c.subscriptionId, acc);
  }

  if (isLoading) {
    return <p className="py-6 text-center text-[13px] text-muted">Carregando assinaturas…</p>;
  }

  if (isError) {
    return (
      <p className="py-6 text-center text-[13px] text-bad">
        Não conseguimos carregar as assinaturas.
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-panel-2 text-muted">
          <Repeat size={18} strokeWidth={1.6} />
        </span>
        <p className="text-[13px] text-muted">
          {isOwlManaged ? (
            <>
              Sem assinaturas Owl-managed.
              <br />
              Vincule um plano comercial pra começar a faturar.
            </>
          ) : (
            <>
              Sem assinaturas vindas direto do SaaS.
              <br />
              Aparecem aqui quando o cliente paga direto no Reservei ou OwlTask.
            </>
          )}
        </p>
        {isOwlManaged && (
          <PillButton
            solid
            icon={<Plus size={14} strokeWidth={2} />}
            onClick={() => navigate(`/subscriptions/new?clientId=${clientId}`)}
          >
            Nova assinatura
          </PillButton>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-semibold uppercase tracking-wider text-muted-3">
          {isOwlManaged ? 'Assinaturas Owl-managed' : 'Assinaturas vindas dos SaaS'}
        </h3>
        {isOwlManaged && (
          <PillButton
            icon={<Plus size={13} strokeWidth={2} />}
            onClick={() => navigate(`/subscriptions/new?clientId=${clientId}`)}
          >
            Adicionar
          </PillButton>
        )}
      </div>

      <ul className="divide-y divide-line rounded-[10px] border border-line bg-panel">
        {items.map((sub) => {
          const tone = STATUS_TONE[sub.status] ?? 'bg-panel-3 text-muted border-line';
          return (
            <li
              key={sub.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/subscriptions/${sub.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') navigate(`/subscriptions/${sub.id}`);
              }}
              className="flex cursor-pointer flex-wrap items-center gap-4 px-4 py-3 transition-colors hover:bg-panel-2"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
                <Package size={13} strokeWidth={1.8} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium text-ink">
                    {sub.plan?.name ?? 'Plano sem nome'}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-[6px] border px-2 py-[1px] text-[10px] font-medium uppercase tracking-wider',
                      tone,
                    )}
                  >
                    {STATUS_LABEL[sub.status] ?? sub.status}
                  </span>
                </div>
                <p className="mt-1 flex items-center gap-2 text-[11px] text-muted-2">
                  <Calendar size={11} strokeWidth={1.8} />
                  Iniciada em {formatDayBR(sub.startDate)}
                  {sub.nextBillingDate && (
                    <>
                      <span>·</span>
                      <span>Próxima cobrança {formatDayBR(sub.nextBillingDate)}</span>
                    </>
                  )}
                </p>
                {(() => {
                  const atraso = overdueBySubscription.get(sub.id);
                  if (!atraso) return null;
                  return (
                    <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-[6px] border border-bad/30 bg-bad/5 px-2 py-[3px] text-[11px] font-medium text-red-300">
                      <AlertTriangle size={11} strokeWidth={2} />
                      {atraso.count === 1
                        ? `1 cobrança vencida · ${formatBRL(String(atraso.total))}`
                        : `${atraso.count} cobranças vencidas · ${formatBRL(String(atraso.total))}`}
                    </p>
                  );
                })()}
              </div>
              <div className="text-right tnum">
                <div className="text-[13px] font-semibold text-ink">{formatBRL(sub.price)}</div>
                <div className="text-[11px] text-muted-2">/ mês</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
